import {PageDataLegacy, PageData} from '~/modules/shared/types/quiz-page.types';
import {QuestionType} from '~/stores/rtk-query/types/quiz.types';
import {nanoid} from 'nanoid';

/**
 * Migrate legacy quiz page data to v2 structure.
 *
 * Pure with respect to its input — returns a brand-new object, does NOT mutate `page`.
 * Input MUST be v1 (no `version` field, or `version === 1`); the only caller
 * (`getParsedCourseData` in `src/utils/course.js`) enforces this guard before calling.
 *
 * Drops/flattens that downstream consumers must be aware of:
 * - `questionnaire.texts.*` → flattened to top-level `textWhenGood` / `textWhenWrong`
 *   on the new questionnaire (any consumer still reading the nested `texts` path
 *   will get undefined).
 * - `questionnaire.settings` → dropped (no replacement; legacy-only).
 * - `questionnaire.timeBounds` → dropped; `timeToAnswerQuestionSec` is hoisted to
 *   top-level `timePerQuestion` on the page.
 * - `question_type` is hardcoded to MULTIPLE_CHOICE — legacy questionnaires only
 *   supported MCQ. Do NOT call this on v2 data.
 */
export const migrateLegacyQuizData = (page: PageDataLegacy): PageData => {
  const questionnaire = page.questionnaire || {};
  const answers = questionnaire.answers || [];
  const texts = questionnaire.texts || {};
  const settings = questionnaire.settings || {};
  const timeBounds = questionnaire.timeBounds || {};

  return {
    id: page.id,
    templateTypeId: page.templateTypeId || 'TEXT-QUESTIONNAIRE',
    title: page.title || '',
    version: 2,
    questionnaire: {
      /**
       * Legacy questions are only multiple choice
       */
      question_type: QuestionType.MULTIPLE_CHOICE,
      question: page.title || '',
      answers: answers.map(answer => ({
        id: answer.id || nanoid(),
        text: answer.text || '',
        isCorrect: answer.isCorrect || false,
      })),
      knowledge: page.knowledge || '',
      textWhenGood: texts.textWhenGood || '',
      textWhenWrong: texts.textWhenWrong || '',
    },
    thumbnailUrl: page.thumbnailUrl || '',
    thumbnailRequested: page.thumbnailRequested || false,

    // Settings
    answerMode: page.answerMode || 'normal',
    timePerQuestion: timeBounds.timeToAnswerQuestionSec ?? 0,
    totalAllowedAttempts: undefined,
    onCorrectGoto: page.onCorrectGoto || null,
    onIncorrectGoto: page.onIncorrectGoto || null,
    goBackDisabled: page.goBackDisabled || false,
    hideProgress: page.hideProgress || false,

    // Design
    defaultFont: page.defaultFont || 'DM Sans',
    bgColor: page.bgColor || '#ffffff',
    bgToColor: page.bgToColor || undefined,
    bgInputType: page.bgInputType || 'solid',
    bgImage: page.bgImage || null,
    backgroundScale: page.backgroundScale ?? 1,
    backgroundScaleMobile: page.backgroundScaleMobile ?? 1,
    backgroundOpacity: page.backgroundOpacity ?? 1,
    overlayImage: page.overlayImage || false,
    backgroundScaleEnabled: page.backgroundScaleEnabled || false,
    backgroundPosition: page.backgroundPosition || 'center',
    txtColor: page.txtColor || null,
    bgImageAsBackground: false,
    bgImageContrast: 'light',
  };
};
